import React, { FC } from 'react';
import { Button } from 'shared/ui/Button';
import { useRole, ProfileVerificationStatus, useGetUserByIdQuery } from 'entities/User';
import { useAppTranslation } from 'app/config/i18Config/hooks';

import { ReactComponent as CheckIcon } from 'shared/assets/icons/CheckMark.svg';
import { ReactComponent as RejectIcon } from 'shared/assets/icons/CancelIcon.svg';
import { ReactComponent as ClockIcon } from 'shared/assets/icons/newIcons/historyActionIcons/InfoIcon.svg';
import { ReactComponent as PassportIcon } from 'shared/assets/icons/newIcons/PassportIcon.svg';

interface Props {
  userId: string;
  onUploadClick: () => void;
}

export const AccountVerificationNotice: FC<Props> = ({ userId, onUploadClick }) => {
  const { isManagement } = useRole();

  const { data: user } = useGetUserByIdQuery({ userId });
  const { t } = useAppTranslation(['profile', 'common']);

  const status = user?.profileVerificationStatus || null;

  const statusInfo = {
    [ProfileVerificationStatus.APPROVED]: {
      color: 'text-green-600',
      bg: 'bg-green-100',
      icon: <CheckIcon className="w-[20px] h-[20px] shrink-0 [&>path]:stroke-green-600" />,
      label: t('Documents are approved'),
    },
    [ProfileVerificationStatus.REJECTED]: {
      color: 'text-red-600',
      bg: 'bg-red-100',
      icon: <RejectIcon className="w-[20px] h-[20px] shrink-0 [&>path]:stroke-red-600" />,
      label: t('Documents are rejected'),
    },
    [ProfileVerificationStatus.PENDING]: {
      color: 'text-orange-500',
      bg: 'bg-orange-100',
      icon: <ClockIcon className="w-[20px] h-[20px] shrink-0 [&>path]:fill-orange-400" />,
      label: t('Documents are being verifying'),
    },
    unverified: {
      color: 'text-gray-500',
      bg: 'bg-gray-100',
      icon: <PassportIcon className="w-[20px] h-[20px] shrink-0" />,
      label: t('Documents are not uploaded'),
    },
  };

  const current = statusInfo[status || 'unverified'];

  if (isManagement) {
    return (
      <div className={`mb-4 flex flex-col md:flex-row md:justify-between md:items-center gap-2 p-3 rounded-lg ${current.bg}`}>
        <div className={`flex items-center gap-2 font-medium ${current.color}`}>
          {current.icon}
          <span>{current.label}</span>
        </div>

        <Button size="small" onClick={onUploadClick} className="border border-gray-300 text-gray-700 hover:bg-green-600">
          {t('View docs')}
        </Button>
      </div>
    );
  }

  if (status === ProfileVerificationStatus.APPROVED) {
    return null;
  }

  const userMessages = {
    unverified: {
      color: 'text-red-500',
      icon: '❗',
      text: (
        <>
          {t('Account is not verified! To complete verification click')}{' '}
          <button onClick={onUploadClick} className="text-red-600 underline hover:text-red-800">
            {t('Here', { ns: 'common' })}
          </button>
        </>
      ),
    },
    pending: {
      color: 'text-orange-500',
      icon: '🕓',
      text: <>{t('Your docs are under review')}</>,
    },
    rejected: {
      color: 'text-red-500',
      icon: '⚠️',
      text: (
        <>
          {t('Docs were rejected. To see reasons, click')}{' '}
          <button onClick={onUploadClick} className="text-red-600 underline hover:text-red-800">
            {t('Here', { ns: 'common' })}
          </button>
        </>
      ),
    },
  };

  const currentUser = userMessages[status || 'unverified'];

  return (
    <div className={`mb-4 font-medium flex items-center gap-2 ${currentUser.color}`}>
      <span>{currentUser.icon}</span>
      <span>{currentUser.text}</span>
    </div>
  );
};
